import { useState, useEffect, useRef, useCallback } from 'react';

const DEFAULT_BATCH = 50;

/**
 * Progressive rendering hook: slices a large array and loads more
 * items when a sentinel element scrolls into view (IntersectionObserver).
 *
 * Returns the visible slice and a callback `sentinelRef` to attach to a
 * placeholder element at the bottom of the list.
 */
export function useInfiniteScroll<T>(
  items: T[],
  batchSize: number = DEFAULT_BATCH,
) {
  const [visibleCount, setVisibleCount] = useState(batchSize);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);

  // Reset when the source array or batch size changes (e.g. new search)
  useEffect(() => {
    setVisibleCount(batchSize);
  }, [items, batchSize]);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + batchSize, items.length));
  }, [batchSize, items.length]);

  // Keep loadMore ref fresh so the observer callback always calls the latest version
  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  // Lazily create the observer once
  const getObserver = useCallback(() => {
    if (observerRef.current) return observerRef.current;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreRef.current();
        }
      },
      { rootMargin: '200px' },
    );
    return observerRef.current;
  }, []);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  // Callback ref: re-observes whenever the sentinel DOM node mounts/changes
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      const observer = getObserver();
      if (sentinelNodeRef.current) {
        observer.unobserve(sentinelNodeRef.current);
      }
      sentinelNodeRef.current = node;
      if (node) {
        observer.observe(node);
      }
    },
    [getObserver],
  );

  const visible = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  return { visible, hasMore, sentinelRef };
}
